import React, { useState, useEffect, useRef } from 'react';
import {
  Card, Form, Input, Switch, InputNumber, Select, Button,
  Alert, Descriptions, Spin, message, Row, Col, Divider, Tag,
} from 'antd';
import {
  SaveOutlined, CheckCircleOutlined, ExclamationCircleOutlined,
  UploadOutlined, SafetyCertificateOutlined,
} from '@ant-design/icons';
import fiscalService from '../../services/fiscalService';

const { Option } = Select;

const ConfiguracaoFiscal = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);

  // Certificado
  const [credencial, setCredencial] = useState(null);
  const [pfxFile, setPfxFile] = useState(null);
  const [certSenha, setCertSenha] = useState('');
  const [uploadingCert, setUploadingCert] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const [statusResp, configResp, credencialResp] = await Promise.allSettled([
        fiscalService.getConfigStatus(),
        fiscalService.getConfig(),
        fiscalService.getCredencial(),
      ]);

      if (statusResp.status === 'fulfilled') {
        setStatus(statusResp.value.data);
      }

      if (configResp.status === 'fulfilled') {
        const cfg = configResp.value.data;
        setConfig(cfg);
        form.setFieldsValue({
          ...cfg,
          aliquota_iss: cfg.aliquota_iss != null ? parseFloat(cfg.aliquota_iss) * 100 : undefined,
        });
      }

      if (credencialResp.status === 'fulfilled') {
        setCredencial(credencialResp.value.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadCertificado = async () => {
    if (!pfxFile) {
      message.warning('Selecione um arquivo .pfx antes de enviar.');
      return;
    }
    if (!certSenha) {
      message.warning('Informe a senha do certificado.');
      return;
    }
    setUploadingCert(true);
    try {
      const formData = new FormData();
      formData.append('pfx_file', pfxFile);
      formData.append('senha', certSenha);
      const resp = await fiscalService.uploadCertificado(formData);
      message.success(
        `Certificado carregado! Válido até ${resp.data.validade}. Emitente: ${resp.data.subject}`,
        8,
      );
      setPfxFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await fetchConfig();
    } catch (err) {
      const detail = err.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : 'Erro ao carregar certificado.');
    } finally {
      setCertSenha('');  // sempre limpa a senha — sucesso ou falha
      setUploadingCert(false);
    }
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const payload = {
        ...values,
        aliquota_iss: values.aliquota_iss != null ? values.aliquota_iss / 100 : null,
        competencia_inicial_nfse: values.competencia_inicial_nfse || null,
      };

      if (config) {
        await fiscalService.updateConfig(config.id, payload);
        message.success('Configuração fiscal atualizada com sucesso.');
      } else {
        await fiscalService.createConfig(payload);
        message.success('Configuração fiscal criada com sucesso.');
      }
      await fetchConfig();
    } catch (err) {
      const detail = err.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : 'Erro ao salvar configuração fiscal.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>
      <h2 style={{ marginBottom: 16 }}>Configuração Fiscal</h2>

      {status && (
        <Alert
          type={status.configurado ? 'success' : 'warning'}
          icon={status.configurado ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
          showIcon
          message={status.mensagem}
          description={
            status.gates_falhos.length > 0 && (
              <span>Pendências: {status.gates_falhos.map(g => (
                <Tag color="orange" key={g} style={{ margin: 2 }}>{g}</Tag>
              ))}</span>
            )
          }
          style={{ marginBottom: 24 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        initialValues={{
          cidade: 'São Paulo',
          uf: 'SP',
          natureza_operacao: 'TRIBUTADA_NO_MUNICIPIO',
          serie_rps: 'A1',
          provider: 'SAO_PAULO_NFSE',
          ambiente: 'PRODUCAO',
          fiscal_ativo: false,
          optante_simples: false,
        }}
      >
        <Card title="Emitente" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item label="Razão Social" name="razao_social" rules={[{ required: true }]}>
                <Input placeholder="FISICORE ASSESSORIA DE DANCA E ESPORTE LTDA" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Nome Fantasia" name="nome_fantasia">
                <Input placeholder="Fisicore" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="CNPJ (só dígitos)" name="cnpj" rules={[{ required: true, len: 14 }]}>
                <Input placeholder="00000000000100" maxLength={14} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Inscrição Municipal" name="inscricao_municipal">
                <Input placeholder="0000000" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Regime Tributário" name="regime_tributario">
                <Select allowClear placeholder="Selecione">
                  <Option value="SIMPLES_NACIONAL">Simples Nacional</Option>
                  <Option value="LUCRO_PRESUMIDO">Lucro Presumido</Option>
                  <Option value="LUCRO_REAL">Lucro Real</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={4}>
              <Form.Item label="Optante Simples" name="optante_simples" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="E-mail" name="email">
                <Input type="email" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Telefone" name="telefone">
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Endereço do Emitente" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={14}>
              <Form.Item label="Logradouro" name="logradouro">
                <Input />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Número" name="numero">
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Complemento" name="complemento">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Bairro" name="bairro">
                <Input />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="CEP" name="cep">
                <Input maxLength={8} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Cidade" name="cidade">
                <Input />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="UF" name="uf">
                <Input maxLength={2} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Serviço Fiscal" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Código de Serviço ISS"
                name="codigo_servico_iss"
                rules={[{
                  pattern: /^[0-9]{4,5}$/,
                  message: 'Informe 4 ou 5 dígitos numéricos sem pontos (Ex.: 05657).',
                }]}
              >
                <Input placeholder="Ex.: 05657" maxLength={5} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Alíquota ISS (%)"
                name="aliquota_iss"
                rules={[{
                  validator: (_, value) => {
                    if (value == null) return Promise.resolve();
                    if (value < 0) return Promise.reject(new Error('A alíquota não pode ser negativa.'));
                    if (value > 100) return Promise.reject(new Error('A alíquota não pode ultrapassar 100%.'));
                    return Promise.resolve();
                  },
                }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={100}
                  step={0.01}
                  precision={2}
                  placeholder="2.00"
                  addonAfter="%"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Natureza da Operação" name="natureza_operacao">
                <Select>
                  <Option value="TRIBUTADA_NO_MUNICIPIO">Tributada no Município</Option>
                  <Option value="TRIBUTADA_FORA_MUNICIPIO">Tributada Fora do Município</Option>
                  <Option value="ISENTA">Isenta</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Descrição do Serviço" name="descricao_servico">
            <Input.TextArea rows={3} placeholder="Prestação de serviços de atividades extracurriculares esportivas (dança e atividades físicas), realizadas em instituição de ensino, conforme contrato firmado." />
          </Form.Item>
        </Card>

        <Card title="Emissão RPS" style={{ marginBottom: 16 }}>
          {config && config.ambiente === 'HOMOLOGACAO' && (
            <Alert
              type="warning"
              showIcon
              message="Ambiente de homologação detectado"
              description="O provider SAO_PAULO_NFSE não possui endpoint de homologação implementado. Toda emissão real é sempre enviada ao WebService de produção da Prefeitura SP, independentemente deste campo. Salve com 'Produção' para refletir a realidade."
              style={{ marginBottom: 16 }}
            />
          )}
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="Série RPS" name="serie_rps">
                <Input maxLength={5} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Provider" name="provider">
                <Select disabled>
                  <Option value="SAO_PAULO_NFSE">São Paulo NFS-e</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Ambiente" name="ambiente">
                <Select disabled>
                  <Option value="PRODUCAO">Produção</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Módulo Ativo" name="fiscal_ativo" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          {config && (
            <Descriptions size="small" bordered column={2} style={{ marginTop: 8 }}>
              <Descriptions.Item label="Próximo nº RPS">{config.proximo_numero_rps}</Descriptions.Item>
              <Descriptions.Item label="ID Configuração">{config.id}</Descriptions.Item>
            </Descriptions>
          )}
        </Card>

        <Card title="Período Fiscal" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Início das emissões pelo ERP"
                name="competencia_inicial_nfse"
                extra="Competências anteriores serão consideradas legado e não poderão gerar NFS-e pelo ERP. Deixe em branco para permitir todas as competências."
                rules={[{
                  pattern: /^\d{4}-(0[1-9]|1[0-2])$/,
                  message: 'Use o formato YYYY-MM (ex.: 2026-06).',
                }]}
              >
                <Input placeholder="2026-06" maxLength={7} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={saving}
            size="large"
          >
            {config ? 'Atualizar Configuração' : 'Criar Configuração'}
          </Button>
        </Form.Item>
      </Form>

      {/* ------------------------------------------------------------------ */}
      {/* Certificado Digital A1                                               */}
      {/* ------------------------------------------------------------------ */}
      <Card
        title={
          <span>
            <SafetyCertificateOutlined style={{ marginRight: 8 }} />
            Certificado Digital A1
          </span>
        }
        style={{ marginTop: 24, marginBottom: 24 }}
      >
        {credencial && credencial.certificado_configurado ? (
          <Alert
            type="success"
            showIcon
            message="Certificado configurado"
            description={
              credencial.validade_certificado
                ? `Válido até ${new Date(credencial.validade_certificado + 'T00:00:00').toLocaleDateString('pt-BR')}`
                : 'Validade não informada.'
            }
            style={{ marginBottom: 16 }}
          />
        ) : (
          <Alert
            type="warning"
            showIcon
            message="Certificado não configurado"
            description="Sem certificado digital, a emissão de NFS-e não será possível."
            style={{ marginBottom: 16 }}
          />
        )}

        <Row gutter={16} align="bottom">
          <Col span={8}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Arquivo .pfx / .p12</div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pfx,.p12"
              onChange={(e) => setPfxFile(e.target.files[0] || null)}
              style={{ display: 'block', marginBottom: 4 }}
            />
            {pfxFile && (
              <div style={{ fontSize: 12, color: '#52c41a' }}>
                Selecionado: {pfxFile.name}
              </div>
            )}
          </Col>
          <Col span={8}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Senha do certificado</div>
            <Input.Password
              value={certSenha}
              onChange={(e) => setCertSenha(e.target.value)}
              placeholder="Senha do arquivo .pfx"
              visibilityToggle
            />
          </Col>
          <Col span={8}>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              loading={uploadingCert}
              onClick={handleUploadCertificado}
              disabled={!pfxFile || !certSenha}
            >
              Carregar Certificado
            </Button>
          </Col>
        </Row>

        <Alert
          type="info"
          showIcon={false}
          message={
            <span style={{ fontSize: 12 }}>
              O certificado é criptografado antes de ser armazenado no banco.
              A senha nunca é gravada em texto plano.
              Requer <code>FISCAL_CERT_KEY</code> configurada no servidor.
            </span>
          }
          style={{ marginTop: 16 }}
        />
      </Card>
    </div>
  );
};

export default ConfiguracaoFiscal;
